import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Copy, Trash2, Edit2, Check, X, GripVertical, ChevronDown, ChevronUp, Loader2, Globe, Eye, EyeOff, FileText, ClipboardList, Star, Circle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { storage, getDefaultTemplate } from '../lib/storage';
import { generateId } from '../lib/utils';
import { fetchSessionParts, createSessionPart, deleteSessionPart } from '../lib/sessionPartsApi';
import { 
  fetchObservationTemplates, 
  updateObservationTemplate, 
  createObservationTemplate,
  deleteObservationTemplate,
  setDefaultObservationTemplate,
  hideAdminTemplate,
  showAdminTemplate,
  getHiddenTemplates,
  setAdminTemplateAsDefault,
  unsetAdminTemplateAsDefault
} from '../lib/observationTemplatesApi';
import { useAuth } from '../contexts/AuthContext';
import ReflectionTemplatesSection from '../components/ReflectionTemplatesSection';
import { SwipeablePageWrapper } from '../components/SwipeablePageWrapper';

export default function TemplateManager() {
  const navigate = useNavigate();
  const { isCoachDeveloper } = useAuth();
  const [activeTab, setActiveTab] = useState('observation');
  const [templates, setTemplates] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Hidden templates state
  const [hiddenTemplates, setHiddenTemplates] = useState([]);
  const [showHiddenDialog, setShowHiddenDialog] = useState(false);
  
  // Global session parts state
  const [globalParts, setGlobalParts] = useState([]);
  const [loadingParts, setLoadingParts] = useState(true);
  
  // Add part dialog state
  const [showAddPartDialog, setShowAddPartDialog] = useState(false);
  const [addPartTemplateId, setAddPartTemplateId] = useState(null);
  const [customPartName, setCustomPartName] = useState('');
  const [addAsGlobalDefault, setAddAsGlobalDefault] = useState(false);
  const [savingPart, setSavingPart] = useState(false);
  
  // Debounce refs for saving
  const saveTimeoutRef = useRef({});
  const pendingChangesRef = useRef({});

  // Debounced save function - waits 800ms after last change before saving
  const debouncedSave = useCallback((templateId, updatedTemplate) => {
    // Store the pending changes
    pendingChangesRef.current[templateId] = updatedTemplate;
    
    // Clear any existing timeout for this template
    if (saveTimeoutRef.current[templateId]) {
      clearTimeout(saveTimeoutRef.current[templateId]);
    }
    
    // Set a new timeout
    saveTimeoutRef.current[templateId] = setTimeout(async () => {
      const templateToSave = pendingChangesRef.current[templateId];
      if (templateToSave) {
        await saveAndRefresh(templateToSave);
        delete pendingChangesRef.current[templateId];
      }
    }, 800);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  useEffect(() => {
    loadTemplates();
    loadGlobalParts();
  }, []);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const apiTemplates = await fetchObservationTemplates();
      if (apiTemplates && apiTemplates.length > 0) {
        setTemplates(apiTemplates);
      } else {
        // Fallback to localStorage if no API templates
        setTemplates(storage.getTemplates());
      }
    } catch (err) {
      console.error('Failed to load templates from API:', err);
      // Fallback to localStorage
      setTemplates(storage.getTemplates());
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadGlobalParts = async () => {
    setLoadingParts(true);
    try {
      const parts = await fetchSessionParts();
      setGlobalParts(parts);
    } catch (err) {
      console.error('Failed to load global session parts:', err);
    } finally {
      setLoadingParts(false);
    }
  };

  // Save template to API and refresh
  const saveAndRefresh = async (template) => {
    setSavingTemplate(true);
    try {
      // Check if this is an API template (has templateId) or localStorage template
      if (template.templateId) {
        // Update via API
        const updated = await updateObservationTemplate(template.templateId, template);
        setTemplates(prev => prev.map(t => t.id === template.id ? updated : t));
        toast.success('Template saved');
      } else {
        // Save to localStorage (legacy support)
        storage.saveTemplate(template);
        setTemplates(storage.getTemplates());
      }
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error(err.message || 'Failed to save template');
      // Fallback to localStorage
      storage.saveTemplate(template);
      setTemplates(storage.getTemplates());
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDuplicate = async (template) => {
    const newTemplate = {
      name: `${template.name} (Copy)`,
      description: template.description,
      observationContext: template.observationContext || 'training',
      interventionTypes: (template.eventTypes || template.interventionTypes || []).map(e => ({ 
        ...e, 
        id: generateId('event') 
      })),
      eventTypes: (template.eventTypes || template.interventionTypes || []).map(e => ({ 
        ...e, 
        id: generateId('event') 
      })),
      descriptorGroup1: {
        ...(template.descriptorGroup1 || { name: 'Group 1', color: 'blue', descriptors: [] }),
        descriptors: (template.descriptorGroup1?.descriptors || []).map(d => ({ ...d, id: generateId('desc') }))
      },
      descriptorGroup2: {
        ...(template.descriptorGroup2 || { name: 'Group 2', color: 'green', descriptors: [] }),
        descriptors: (template.descriptorGroup2?.descriptors || []).map(d => ({ ...d, id: generateId('desc') }))
      },
      sessionParts: (template.sessionParts || []).map((p, idx) => ({ 
        ...p, 
        id: generateId('part'),
        order: idx
      })),
      isDefault: false
    };
    
    try {
      const created = await createObservationTemplate(newTemplate);
      setTemplates(prev => [created, ...prev]);
      setExpandedId(created.id);
      toast.success('Template duplicated');
    } catch (err) {
      console.error('Failed to duplicate template:', err);
      // Fallback to localStorage
      const localTemplate = {
        ...newTemplate,
        id: generateId('template')
      };
      storage.saveTemplate(localTemplate);
      setTemplates(storage.getTemplates());
      setExpandedId(localTemplate.id);
      toast.success('Template duplicated (saved locally)');
    }
  };

  const handleDelete = async (templateId, template) => {
    try {
      if (template.templateId) {
        await deleteObservationTemplate(template.templateId);
      } else {
        storage.deleteTemplate(templateId);
      }
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (err) {
      console.error('Failed to delete template:', err);
      toast.error(err.message || 'Failed to delete template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      if (template.isAdminTemplate) {
        // For admin templates, use the user preference endpoint
        await setAdminTemplateAsDefault(template.templateId);
        setTemplates(prev => prev.map(t => ({
          ...t,
          isDefault: t.id === template.id ? true : (t.observationContext === template.observationContext ? false : t.isDefault)
        })));
        toast.success(`"${template.name}" is now your default template`);
      } else if (template.templateId) {
        await setDefaultObservationTemplate(template.templateId);
        // Update local state to reflect the change
        setTemplates(prev => prev.map(t => ({
          ...t,
          isDefault: t.id === template.id
        })));
        toast.success(`"${template.name}" is now the default template`);
      }
    } catch (err) {
      console.error('Failed to set default template:', err);
      toast.error(err.message || 'Failed to set default template');
    }
  };

  const handleHideTemplate = async (template) => {
    try {
      await hideAdminTemplate(template.templateId);
      // Remove from local state
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      toast.success(`"${template.name}" has been hidden. You can restore it from the menu.`);
    } catch (err) {
      console.error('Failed to hide template:', err);
      toast.error(err.message || 'Failed to hide template');
    }
  };

  const handleShowTemplate = async (templateId) => {
    try {
      await showAdminTemplate(templateId);
      // Reload templates to include the unhidden one
      await loadTemplates();
      // Update hidden templates list
      setHiddenTemplates(prev => prev.filter(t => t.template_id !== templateId));
      toast.success('Template restored');
    } catch (err) {
      console.error('Failed to show template:', err);
      toast.error(err.message || 'Failed to restore template');
    }
  };

  const loadHiddenTemplates = async () => {
    try {
      const result = await getHiddenTemplates();
      setHiddenTemplates(result.hidden_templates || []);
    } catch (err) {
      console.error('Failed to load hidden templates:', err);
    }
  };

  const handleOpenHiddenDialog = async () => {
    await loadHiddenTemplates();
    setShowHiddenDialog(true);
  };

  const handleCreateNew = async () => {
    const defaultTemplate = getDefaultTemplate();
    const newTemplate = {
      name: 'New Template',
      description: '',
      observationContext: 'training',
      interventionTypes: (defaultTemplate.eventTypes || defaultTemplate.interventionTypes || []).map(e => ({ 
        ...e, 
        id: generateId('event') 
      })),
      eventTypes: (defaultTemplate.eventTypes || defaultTemplate.interventionTypes || []).map(e => ({ 
        ...e, 
        id: generateId('event') 
      })),
      descriptorGroup1: {
        ...(defaultTemplate.descriptorGroup1 || { name: 'Content Focus', color: 'blue', descriptors: [] }),
        descriptors: (defaultTemplate.descriptorGroup1?.descriptors || []).map(d => ({ ...d, id: generateId('desc') }))
      },
      descriptorGroup2: {
        ...(defaultTemplate.descriptorGroup2 || { name: 'Delivery Method', color: 'green', descriptors: [] }),
        descriptors: (defaultTemplate.descriptorGroup2?.descriptors || []).map(d => ({ ...d, id: generateId('desc') }))
      },
      sessionParts: [
        { id: generateId('part'), name: 'Part 1', order: 0 },
        { id: generateId('part'), name: 'Part 2', order: 1 },
        { id: generateId('part'), name: 'Part 3', order: 2 },
        { id: generateId('part'), name: 'Part 4', order: 3 }
      ],
      isDefault: false
    };
    
    try {
      const created = await createObservationTemplate(newTemplate);
      setTemplates(prev => [created, ...prev]);
      setExpandedId(created.id);
      toast.success('Template created');
    } catch (err) {
      console.error('Failed to create template:', err);
      // Fallback to localStorage
      const localTemplate = {
        ...newTemplate,
        id: generateId('template')
      };
      storage.saveTemplate(localTemplate);
      setTemplates(storage.getTemplates());
      setExpandedId(localTemplate.id);
      toast.success('Template created (saved locally)');
    }
  };

  // Update template name - uses debounced save to prevent jumbling
  const updateTemplateName = (templateId, name) => {
    // Update local state immediately for responsive UI
    setTemplates(prev => prev.map(t => 
      t.id === templateId ? { ...t, name } : t
    ));
    
    // Debounce the actual save
    const template = templates.find(t => t.id === templateId);
    if (template) {
      debouncedSave(templateId, { ...template, name });
    }
  };

  // Event type functions
  const addEventType = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const newEvent = { id: generateId('event'), name: 'New Intervention', color: 'yellow' };
      const updatedEvents = [...(template.eventTypes || template.interventionTypes || []), newEvent];
      saveAndRefresh({ 
        ...template, 
        eventTypes: updatedEvents,
        interventionTypes: updatedEvents 
      });
    }
  };

  const updateEventType = (templateId, eventId, name) => {
    // Update local state immediately for responsive UI
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      const events = t.eventTypes || t.interventionTypes || [];
      const updatedEvents = events.map(e => e.id === eventId ? { ...e, name } : e);
      return { ...t, eventTypes: updatedEvents, interventionTypes: updatedEvents };
    }));
    
    // Debounce the actual save
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const events = template.eventTypes || template.interventionTypes || [];
      const updatedEvents = events.map(e => e.id === eventId ? { ...e, name } : e);
      debouncedSave(templateId, {
        ...template,
        eventTypes: updatedEvents,
        interventionTypes: updatedEvents
      });
    }
  };

  const removeEventType = (templateId, eventId) => {
    const template = templates.find(t => t.id === templateId);
    const events = template?.eventTypes || template?.interventionTypes || [];
    if (template && events.length > 1) {
      const updatedEvents = events.filter(e => e.id !== eventId);
      saveAndRefresh({
        ...template,
        eventTypes: updatedEvents,
        interventionTypes: updatedEvents
      });
    } else {
      toast.error('Need at least one event type');
    }
  };

  // Descriptor group functions
  const updateGroupName = (templateId, groupNum, name) => {
    const key = `descriptorGroup${groupNum}`;
    
    // Update local state immediately for responsive UI
    setTemplates(prev => prev.map(t => 
      t.id === templateId ? { ...t, [key]: { ...t[key], name } } : t
    ));
    
    // Debounce the actual save
    const template = templates.find(t => t.id === templateId);
    if (template) {
      debouncedSave(templateId, {
        ...template,
        [key]: { ...template[key], name }
      });
    }
  };

  const addDescriptor = (templateId, groupNum) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const key = `descriptorGroup${groupNum}`;
      const newDesc = { id: generateId('desc'), name: 'New' };
      saveAndRefresh({
        ...template,
        [key]: { ...template[key], descriptors: [...template[key].descriptors, newDesc] }
      });
    }
  };

  const updateDescriptor = (templateId, groupNum, descId, name) => {
    const key = `descriptorGroup${groupNum}`;
    
    // Update local state immediately for responsive UI
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      const group = t[key] || { name: '', descriptors: [] };
      return {
        ...t,
        [key]: {
          ...group,
          descriptors: (group.descriptors || []).map(d => d.id === descId ? { ...d, name } : d)
        }
      };
    }));
    
    // Debounce the actual save
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const group = template[key] || { name: '', descriptors: [] };
      debouncedSave(templateId, {
        ...template,
        [key]: {
          ...group,
          descriptors: (group.descriptors || []).map(d => d.id === descId ? { ...d, name } : d)
        }
      });
    }
  };

  const removeDescriptor = (templateId, groupNum, descId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const key = `descriptorGroup${groupNum}`;
      const group = template[key] || { name: '', descriptors: [] };
      saveAndRefresh({
        ...template,
        [key]: {
          ...group,
          descriptors: (group.descriptors || []).filter(d => d.id !== descId)
        }
      });
    }
  };

  // Session parts functions
  const openAddPartDialog = (templateId) => {
    setAddPartTemplateId(templateId);
    setCustomPartName('');
    setAddAsGlobalDefault(false);
    setShowAddPartDialog(true);
  };

  const handleSaveNewPart = async () => {
    if (!customPartName.trim()) {
      toast.error('Please enter a part name');
      return;
    }

    const template = templates.find(t => t.id === addPartTemplateId);
    if (!template) return;

    setSavingPart(true);
    try {
      if (addAsGlobalDefault && isCoachDeveloper()) {
        const newGlobalPart = await createSessionPart(customPartName.trim(), true);
        await loadGlobalParts();
        
        const parts = template.sessionParts || [];
        const newPart = { 
          id: generateId('part'), 
          name: customPartName.trim(), 
          order: parts.length,
          partId: newGlobalPart.part_id,
          isDefault: true
        };
        saveAndRefresh({ ...template, sessionParts: [...parts, newPart] });
        toast.success('Session part added as organization default');
      } else {
        const parts = template.sessionParts || [];
        const newPart = { 
          id: generateId('part'), 
          name: customPartName.trim(), 
          order: parts.length,
          isCustom: true
        };
        saveAndRefresh({ ...template, sessionParts: [...parts, newPart] });
        toast.success('Custom session part added');
      }
      
      setShowAddPartDialog(false);
      setCustomPartName('');
    } catch (err) {
      toast.error(err.message || 'Failed to create session part');
    } finally {
      setSavingPart(false);
    }
  };

  const addGlobalPartToTemplate = (templateId, globalPart) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    const parts = template.sessionParts || [];
    if (parts.some(p => p.name === globalPart.name)) {
      toast.error('This part is already in the template');
      return;
    }

    const newPart = { 
      id: generateId('part'), 
      name: globalPart.name, 
      order: parts.length,
      partId: globalPart.part_id,
      isDefault: true
    };
    saveAndRefresh({ ...template, sessionParts: [...parts, newPart] });
    toast.success(`Added "${globalPart.name}" to template`);
  };

  const addSessionPart = (templateId) => {
    openAddPartDialog(templateId);
  };

  const updateSessionPart = (templateId, partId, name) => {
    // Update local state immediately for responsive UI
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      return {
        ...t,
        sessionParts: (t.sessionParts || []).map(p => p.id === partId ? { ...p, name } : p)
      };
    }));
    
    // Debounce the actual save
    const template = templates.find(t => t.id === templateId);
    if (template) {
      debouncedSave(templateId, {
        ...template,
        sessionParts: (template.sessionParts || []).map(p => p.id === partId ? { ...p, name } : p)
      });
    }
  };

  const removeSessionPart = (templateId, partId) => {
    const template = templates.find(t => t.id === templateId);
    if (template && (template.sessionParts || []).length > 1) {
      saveAndRefresh({
        ...template,
        sessionParts: (template.sessionParts || []).filter(p => p.id !== partId)
      });
    } else {
      toast.error('Need at least one session part');
    }
  };

  // Ball rolling toggle handler
  const handleToggleBallRolling = (templateId, checked) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      saveAndRefresh({
        ...template,
        includeBallRolling: checked
      });
    }
  };

  return (
    <SwipeablePageWrapper>
      <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn" className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-slate-900 font-['Manrope']">Templates</h1>
              <p className="text-xs sm:text-sm text-slate-500 truncate">Manage observation and reflection templates</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Top-level Tab Toggle */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6 h-auto" data-testid="template-type-tabs">
            <TabsTrigger value="observation" className="flex items-center gap-1 sm:gap-2 py-2 text-xs sm:text-sm" data-testid="observation-tab">
              <Eye className="w-4 h-4" />
              Observation
            </TabsTrigger>
            <TabsTrigger value="reflection" className="flex items-center gap-1 sm:gap-2 py-2 text-xs sm:text-sm" data-testid="reflection-tab">
              <ClipboardList className="w-4 h-4" />
              Reflection
            </TabsTrigger>
          </TabsList>

          {/* Observation Templates Tab */}
          <TabsContent value="observation" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleOpenHiddenDialog}
                className="text-slate-500"
                data-testid="view-hidden-btn"
              >
                <EyeOff className="w-4 h-4 mr-2" />
                View Hidden
              </Button>
              <Button onClick={handleCreateNew} data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>

            {loadingTemplates ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2" />
                <p className="text-slate-500">Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <Card className="py-12 text-center">
                <p className="text-slate-500 mb-4">No observation templates yet</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Template
                </Button>
              </Card>
            ) : (
            <div className="space-y-4">
              {templates.map((template) => (
                <Collapsible
                  key={template.id}
                  open={expandedId === template.id}
                  onOpenChange={(open) => setExpandedId(open ? template.id : null)}
                >
                  <Card data-testid={`template-card-${template.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {template.isAdminTemplate ? (
                              <span className="font-semibold text-lg" data-testid={`template-name-${template.id}`}>
                                {template.name}
                              </span>
                            ) : (
                              <Input
                                value={template.name}
                                onChange={(e) => updateTemplateName(template.id, e.target.value)}
                                className="font-semibold text-lg border-0 p-0 h-auto focus-visible:ring-0 max-w-xs"
                                data-testid={`template-name-${template.id}`}
                              />
                            )}
                            {template.isDefault && (
                              <Badge className="bg-amber-100 text-amber-700">Default</Badge>
                            )}
                            {template.isGlobal && (
                              <Badge className="bg-purple-100 text-purple-700">Global</Badge>
                            )}
                            {template.isAdminTemplate && !template.isGlobal && (
                              <Badge className="bg-blue-100 text-blue-700">Admin</Badge>
                            )}
                            {template.observationContext && (
                              <Badge variant="outline" className="capitalize">
                                {template.observationContext}
                              </Badge>
                            )}
                            {(template.qualificationTags || []).map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs bg-slate-50">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <CardDescription className="mt-1">
                            {(template.eventTypes || template.interventionTypes || []).length} interventions • {((template.descriptorGroup1?.descriptors || []).length + (template.descriptorGroup2?.descriptors || []).length)} descriptors • {(template.sessionParts || []).length} parts
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-1">
                          <CollapsibleTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`expand-template-${template.id}`}>
                              {expandedId === template.id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                          {/* Set as default - for regular templates */}
                          {!template.isDefault && template.templateId && !template.isAdminTemplate && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleSetDefault(template)}
                              title="Set as default"
                              data-testid={`set-default-${template.id}`}
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Set as default - for admin templates */}
                          {!template.isDefault && template.isAdminTemplate && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleSetDefault(template)}
                              title="Set as your default"
                              data-testid={`set-default-${template.id}`}
                              className="text-purple-600 hover:text-purple-700"
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDuplicate(template)}
                            title={template.isAdminTemplate ? "Make a copy to customize" : "Duplicate template"}
                            data-testid={`duplicate-template-${template.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {/* Hide button for admin templates */}
                          {template.isAdminTemplate && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleHideTemplate(template)}
                              title="Hide this template"
                              className="text-slate-400 hover:text-slate-600"
                              data-testid={`hide-template-${template.id}`}
                            >
                              <EyeOff className="w-4 h-4" />
                            </Button>
                          )}
                          {!template.isDefault && !template.isAdminTemplate && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Template?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete &quot;{template.name}&quot;. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(template.id, template)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid={`confirm-delete-${template.id}`}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-6">
                        {/* Admin template notice */}
                        {template.isAdminTemplate && (
                          <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                            <Globe className="w-4 h-4 flex-shrink-0" />
                            <span>This is a global template managed by your organization admin. Click the copy button above to create your own customizable version.</span>
                          </div>
                        )}
                        
                        {/* Event Types */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded bg-yellow-400" />
                              Coaching Interventions
                            </h4>
                            {!template.isAdminTemplate && (
                              <Button size="sm" variant="outline" onClick={() => addEventType(template.id)}>
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            )}
                          </div>
                          <div className="space-y-2">
                            {(template.eventTypes || template.interventionTypes || []).map(event => (
                              <div key={event.id} className="flex items-center gap-2">
                                {template.isAdminTemplate ? (
                                  <span className="flex-1 px-3 py-2 text-slate-600 bg-slate-50 rounded-md">{event.name}</span>
                                ) : (
                                  <>
                                    <Input
                                      value={event.name}
                                      onChange={(e) => updateEventType(template.id, event.id, e.target.value)}
                                      className="flex-1"
                                      data-testid={`event-${template.id}-${event.id}`}
                                    />
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="text-slate-400 hover:text-red-600 h-8 w-8"
                                      onClick={() => removeEventType(template.id, event.id)}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            ))}
                            {(template.eventTypes || template.interventionTypes || []).length === 0 && (
                              <p className="text-sm text-slate-400 italic">No interventions added yet. Click "Add" to create one.</p>
                            )}
                          </div>
                        </div>

                        {/* Descriptor Group 1 */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded bg-sky-400" />
                              {template.isAdminTemplate ? (
                                <span className="font-medium">{template.descriptorGroup1?.name || 'Group 1'}</span>
                              ) : (
                                <Input
                                  value={template.descriptorGroup1?.name || 'Group 1'}
                                  onChange={(e) => updateGroupName(template.id, 1, e.target.value)}
                                  className="font-medium border-0 p-0 h-auto focus-visible:ring-0 w-40"
                                />
                              )}
                            </div>
                            {!template.isAdminTemplate && (
                              <Button size="sm" variant="outline" onClick={() => addDescriptor(template.id, 1)}>
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(template.descriptorGroup1?.descriptors || []).map(desc => (
                              <div key={desc.id} className={`flex items-center gap-1 bg-sky-100 rounded-lg ${template.isAdminTemplate ? 'px-3 py-1' : 'pl-3 pr-1 py-1'}`}>
                                {template.isAdminTemplate ? (
                                  <span className="text-sm">{desc.name}</span>
                                ) : (
                                  <>
                                    <Input
                                      value={desc.name}
                                      onChange={(e) => updateDescriptor(template.id, 1, desc.id, e.target.value)}
                                      className="border-0 bg-transparent p-0 h-auto w-20 focus-visible:ring-0 text-sm"
                                    />
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-slate-400 hover:text-red-600"
                                      onClick={() => removeDescriptor(template.id, 1, desc.id)}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Descriptor Group 2 */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded bg-green-400" />
                              {template.isAdminTemplate ? (
                                <span className="font-medium">{template.descriptorGroup2?.name || 'Group 2'}</span>
                              ) : (
                                <Input
                                  value={template.descriptorGroup2?.name || 'Group 2'}
                                  onChange={(e) => updateGroupName(template.id, 2, e.target.value)}
                                  className="font-medium border-0 p-0 h-auto focus-visible:ring-0 w-40"
                                />
                              )}
                            </div>
                            {!template.isAdminTemplate && (
                              <Button size="sm" variant="outline" onClick={() => addDescriptor(template.id, 2)}>
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(template.descriptorGroup2?.descriptors || []).map(desc => (
                              <div key={desc.id} className={`flex items-center gap-1 bg-green-100 rounded-lg ${template.isAdminTemplate ? 'px-3 py-1' : 'pl-3 pr-1 py-1'}`}>
                                {template.isAdminTemplate ? (
                                  <span className="text-sm">{desc.name}</span>
                                ) : (
                                  <>
                                    <Input
                                      value={desc.name}
                                      onChange={(e) => updateDescriptor(template.id, 2, desc.id, e.target.value)}
                                      className="border-0 bg-transparent p-0 h-auto w-20 focus-visible:ring-0 text-sm"
                                    />
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-slate-400 hover:text-red-600"
                                      onClick={() => removeDescriptor(template.id, 2, desc.id)}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Session Parts */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-slate-700">Session Parts</h4>
                            {!template.isAdminTemplate && (
                              <Button size="sm" variant="outline" onClick={() => addSessionPart(template.id)} data-testid={`add-part-btn-${template.id}`}>
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            {(template.sessionParts || []).length === 0 ? (
                              <p className="text-sm text-slate-400 italic py-2">No session parts defined.</p>
                            ) : (
                              (template.sessionParts || []).map((part, index) => (
                                <div key={part.id} className="flex items-center gap-2">
                                  <span className="text-sm text-slate-400 w-6">{index + 1}</span>
                                  {template.isAdminTemplate ? (
                                    <span className="flex-1 px-3 py-2 text-slate-600 bg-slate-50 rounded-md">{part.name}</span>
                                  ) : (
                                    <>
                                      <Input
                                        value={part.name}
                                        onChange={(e) => updateSessionPart(template.id, part.id, e.target.value)}
                                        className="flex-1"
                                        placeholder="Part name"
                                        data-testid={`part-${template.id}-${part.id}`}
                                      />
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="text-slate-400 hover:text-red-600 h-8 w-8"
                                        onClick={() => removeSessionPart(template.id, part.id)}
                                        title="Remove this part"
                                        data-testid={`remove-part-${template.id}-${part.id}`}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Ball Rolling Toggle - Only show for non-admin templates */}
                        {!template.isAdminTemplate && (
                          <div>
                            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                                  <Circle className="w-4 h-4 text-orange-600 fill-orange-600" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-slate-700">Include Ball Rolling</h4>
                                  <p className="text-xs text-slate-500">Track ball rolling / stopped time</p>
                                </div>
                              </div>
                              <Switch
                                checked={template.includeBallRolling !== false}
                                onCheckedChange={(checked) => handleToggleBallRolling(template.id, checked)}
                                data-testid={`ball-rolling-toggle-${template.id}`}
                              />
                            </div>
                          </div>
                        )}

                        {/* Ball Rolling Status - Show as read-only for admin templates */}
                        {template.isAdminTemplate && (
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                              <Circle className="w-4 h-4 text-orange-600 fill-orange-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-slate-700">Ball Rolling Tracking</h4>
                              <p className="text-xs text-slate-500">
                                {template.includeBallRolling !== false ? 'Enabled' : 'Disabled'} for this template
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
            )}
          </TabsContent>

          {/* Reflection Templates Tab */}
          <TabsContent value="reflection">
            <ReflectionTemplatesSection />
          </TabsContent>
        </Tabs>

        {/* Add Session Part Dialog */}
        <Dialog open={showAddPartDialog} onOpenChange={setShowAddPartDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Session Part</DialogTitle>
              <DialogDescription>
                Create a new session part for this template.
                {isCoachDeveloper() && " Coach Developers can also add it as an organization-wide default."}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label htmlFor="custom-part-name">Part Name</Label>
                <Input
                  id="custom-part-name"
                  value={customPartName}
                  onChange={(e) => setCustomPartName(e.target.value)}
                  placeholder="e.g., Warm Up, Cool Down"
                  className="mt-1"
                  data-testid="new-part-name-input"
                />
              </div>
              
              {isCoachDeveloper() && (
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="add-as-default"
                    checked={addAsGlobalDefault}
                    onCheckedChange={setAddAsGlobalDefault}
                    data-testid="add-as-default-checkbox"
                  />
                  <Label 
                    htmlFor="add-as-default" 
                    className="text-sm text-slate-600 cursor-pointer flex items-center gap-2"
                  >
                    <Globe className="w-4 h-4" />
                    Add as organization default (available in all sessions for your team)
                  </Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddPartDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveNewPart}
                disabled={savingPart || !customPartName.trim()}
                data-testid="save-new-part-btn"
              >
                {savingPart && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {addAsGlobalDefault ? 'Add as Global Default' : 'Add to Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Hidden Templates Dialog */}
        <Dialog open={showHiddenDialog} onOpenChange={setShowHiddenDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Hidden Templates</DialogTitle>
              <DialogDescription>
                Global templates you&apos;ve hidden. Click restore to make them visible again.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {hiddenTemplates.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No hidden templates</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {hiddenTemplates.map((template) => (
                    <div 
                      key={template.template_id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-sm">{template.name}</p>
                        <p className="text-xs text-slate-500 capitalize">{template.category?.replace('_', ' ')}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShowTemplate(template.template_id)}
                        data-testid={`restore-template-${template.template_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHiddenDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
    </SwipeablePageWrapper>
  );
}
